import { Paper, BottomNavigation, BottomNavigationAction, Badge, Box } from '@mui/material';
import { useGlobalNavigation } from '@/shared/hooks/useGlobalNavigation';
import { useUnreadCount } from '@/features/messages/hooks/useUnreadCount';
import { useFavoritesCountContext } from '@/features/favorites/context/FavoritesCountContext';
import { useSnackbar } from '@/shared/hooks/useSnackbar';

export function GlobalFooter() {
  const { activeTab, handleNavigate, navigationItems } = useGlobalNavigation();
  const { showSnackbar } = useSnackbar();

  const { count: unreadCount, hasError } = useUnreadCount({
    enabled: true,
    onError: () => {
      showSnackbar('Unable to fetch unread messages. Please check your connection.', {
        variant: 'error',
      });
    },
  });

  const { count: favoritesCount } = useFavoritesCountContext();

  const formatBadgeContent = (count: number): string => {
    if (count === 0) return '';
    if (count > 9) return '9+';
    return count.toString();
  };

  return (
    <Paper
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        paddingBottom: 'env(safe-area-inset-bottom)',
        backgroundColor: '#FFFFFF',
      }}
      elevation={3}
    >
      {hasError && (
        <Box
          sx={{
            position: 'absolute',
            top: 4,
            right: 4,
            width: 4,
            height: 4,
            borderRadius: '50%',
            backgroundColor: 'error.main',
            animation: 'pulse 2s infinite',
            '@keyframes pulse': {
              '0%, 100%': { opacity: 1 },
              '50%': { opacity: 0.5 },
            },
          }}
          data-testid="footer-connection-status"
        />
      )}
      <BottomNavigation
        value={activeTab}
        onChange={handleNavigate}
        showLabels
        sx={{
          px: 1,
          '& .MuiBottomNavigationAction-root': {
            minWidth: 'auto',
            px: 1,
          },
        }}
      >
        {navigationItems.map(item => (
          <BottomNavigationAction
            key={item.value}
            label={item.label}
            value={item.value}
            icon={
              item.value === 'messages' ? (
                <Badge
                  badgeContent={formatBadgeContent(unreadCount)}
                  invisible={unreadCount === 0}
                  sx={{
                    '& .MuiBadge-badge': {
                      backgroundColor: 'secondary.main',
                      color: 'secondary.contrastText',
                      fontSize: '0.625rem',
                      height: '16px',
                      minWidth: '16px',
                      padding: '0 4px',
                      fontWeight: 600,
                    },
                  }}
                  data-testid="footer-messages-badge"
                >
                  {item.icon}
                </Badge>
              ) : item.value === 'favorites' ? (
                <Badge
                  badgeContent={formatBadgeContent(favoritesCount)}
                  invisible={favoritesCount === 0}
                  sx={{
                    '& .MuiBadge-badge': {
                      backgroundColor: 'secondary.main',
                      color: 'secondary.contrastText',
                      fontSize: '0.625rem',
                      height: '16px',
                      minWidth: '16px',
                      padding: '0 4px',
                      fontWeight: 600,
                    },
                  }}
                  data-testid="footer-favorites-badge"
                >
                  {item.icon}
                </Badge>
              ) : (
                item.icon
              )
            }
            aria-label={item.ariaLabel}
            data-testid={`footer-nav-${item.value}`}
          />
        ))}
      </BottomNavigation>
    </Paper>
  );
}
